import {Alert, Badge, Button, Group, Text} from '@mantine/core'
import {notifications} from '@mantine/notifications'
import {IconCircleCheck} from '@tabler/icons-react'
import {addDays} from 'date-fns'
import {useState} from 'react'

import {businessCalendarDate} from 'src/api/business-time'
import {toApiDateString} from 'src/jobs/job-schedule-format'

import {useConfirmPickupReady} from './use-work'

type PickupReadyCheckProps = {
    projectId: string
    jobId: string
    startDate: string
    dateTbc: boolean
    confirmedAt: string | null
    confirmedByName: string | null
    canConfirm: boolean
    onConfirmed?: () => void
}

export function PickupReadyCheck({
    projectId,
    jobId,
    startDate,
    dateTbc,
    confirmedAt,
    confirmedByName,
    canConfirm,
    onConfirmed,
}: PickupReadyCheckProps) {
    const confirm = useConfirmPickupReady(projectId, jobId)
    const [error, setError] = useState<string | null>(null)

    const confirmation = confirm.data
    const isConfirmed = confirmedAt !== null || confirmation !== undefined
    const shownConfirmedBy = confirmedByName ?? confirmation?.pickupConfirmedByName ?? null

    if (dateTbc) {
        return null
    }
    if (isConfirmed) {
        return (
            <Badge
                color="green"
                variant="light"
                leftSection={<IconCircleCheck size={14} />}
                data-testid="pickup-confirmed"
            >
                Pickup confirmed{shownConfirmedBy ? ` by ${shownConfirmedBy}` : ''}
            </Badge>
        )
    }

    async function handleConfirm() {
        setError(null)
        try {
            await confirm.mutateAsync()
            notifications.show({
                color: 'green',
                title: 'Pickup confirmed',
                message: 'Marked ready to collect.',
            })
            onConfirmed?.()
        } catch (caught) {
            setError(caught instanceof Error ? caught.message : 'Could not confirm the pickup')
        }
    }

    const tomorrow = startDate === toApiDateString(addDays(businessCalendarDate(), 1))
    return (
        <Alert
            color={tomorrow ? 'orange' : 'gray'}
            variant="light"
            title={tomorrow ? 'Pickup tomorrow not confirmed' : 'Pickup not confirmed yet'}
            data-testid="pickup-not-confirmed"
        >
            <Group justify="space-between" gap="sm">
                <Text size="sm">
                    Confirm the equipment is ready for pickup. If it was released early, use Pickup
                    sooner.
                </Text>
                {canConfirm ? (
                    <Button
                        size="xs"
                        onClick={() => void handleConfirm()}
                        loading={confirm.isPending}
                    >
                        Confirm pickup
                    </Button>
                ) : null}
            </Group>
            {error ? (
                <Text size="sm" c="red" mt="xs">
                    {error}
                </Text>
            ) : null}
        </Alert>
    )
}
